<template>
  <div class="diamond-analyzer-container">
    <div class="content-section">
      <h1>Methanogenic Archaeal Culturomics Toolkit</h1>
      
      <div class="description">
        <h2>About This Tool</h2>
        <p>This tool uses the Diamond sequence alignment algorithm to compare uploaded protein sequences with reference sequence databases to predict microbial metabolic pathways, salt tolerance characteristics, and optimal growth temperature (OGT).</p>
        
        <p class="additional-info">
          Diamond is a high-speed protein sequence alignment tool that determines the most likely metabolic pathways used by microorganisms by comparing your uploaded protein sequences with known reference sequences. The integrated Tome tool predicts the optimal growth temperature of microorganisms. This tool can be used for:
        </p>
        <ul class="feature-list">
          <li><strong>Metabolic Pathway Prediction:</strong> Identify methane, sulfur, and nitrogen metabolism types in microorganisms</li>
          <li><strong>Salt Tolerance Analysis:</strong> Predict whether the microorganism has salt tolerance genes</li>
          <li><strong>Temperature Adaptation Prediction:</strong> Predict optimal growth temperature, distinguishing thermophiles, mesophiles, or psychrophiles</li>
          <li><strong>Functional Analysis:</strong> Understand the biochemical characteristics of microorganisms</li>
          <li><strong>Ecological Research:</strong> Analyze the role of microorganisms in various environments</li>
        </ul>
        
        <el-alert
          title="Processing Time Notice"
          type="info"
          :closable="false"
          show-icon
        >
          <p>Analysis may take several minutes, depending on the size of the protein sequence file. Please be patient.</p>
        </el-alert>
      </div>

      <div class="upload-section">
        <h2>Upload Protein Sequence File</h2>
        <p class="upload-info">Please upload a protein sequence file in FASTA format (.fa, .fasta, .faa). File size limit: 50MB.</p>
        
        <el-upload
          class="upload-demo"
          drag
          action="/api/tools/diamond/upload/"
          :on-success="handleSuccess"
          :on-error="handleError"
          :before-upload="beforeUpload"
          :on-progress="handleProgress"
          :multiple="false"
          :limit="1"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            Drop file here or <em>click to upload</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">
              Supported file formats: .fa, .fasta, .faa (max 50MB)
            </div>
          </template>
        </el-upload>

        <!-- Upload progress display -->
        <div v-if="uploading" class="progress-section">
          <el-progress 
            :percentage="uploadProgress" 
            :status="uploadProgress === 100 ? 'success' : ''"
            :format="progressFormat"
          />
          <p class="progress-text">{{ progressText }}</p>
        </div>
        
        <!-- Start analysis button -->
        <div v-if="showAnalysisOptions" class="start-analysis-section">
          <el-button 
            type="primary" 
            @click="startAnalysis" 
            :loading="analyzing"
            :disabled="!canAnalyze"
            class="start-analysis-button"
          >
            Start Analysis
          </el-button>
        </div>
      </div>

      <!-- Results display area -->
      <div v-if="results.summary" class="results-section">
        <h2>Analysis Results</h2>
        
        <div v-if="results.warningMessage" class="warning-message">
          <el-alert
            :title="results.warningMessage"
            type="warning"
            :closable="false"
            show-icon
          />
        </div>
        
        <!-- 添加雷达图和饼状图 -->
        <div class="radar-chart-container">
          <div class="radar-chart-wrapper">
            <v-chart class="radar-chart" :option="radarChartOptions" autoresize ref="radarChart" />
            <!-- 添加下载按钮 -->
            <div class="chart-actions">
              <el-dropdown @command="handleDownloadChart" split-button type="primary" size="small">
                <el-icon><Download /></el-icon>
                Download Radar Chart
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="png">PNG Format</el-dropdown-item>
                    <el-dropdown-item command="svg">SVG Format</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
          
          <!-- 添加极坐标柱状图 -->
          <div class="polar-chart-wrapper" v-if="findTopMethanePathways && findTopMethanePathways.pathways && findTopMethanePathways.pathways.length > 0">
            <v-chart class="polar-chart" :option="polarChartOptions" autoresize ref="polarChart" />
            <!-- 添加下载按钮 -->
            <div class="chart-actions">
              <el-dropdown @command="handleDownloadPolarChart" split-button type="primary" size="small">
                <el-icon><Download /></el-icon>
                Download Methane Pathways Chart
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="png">PNG Format</el-dropdown-item>
                    <el-dropdown-item command="svg">SVG Format</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
          
          <!-- 添加饼状图 -->
          <div class="pie-chart-wrapper" v-if="results.checkm2_results">
            <v-chart class="pie-chart" :option="pieChartOptions" autoresize ref="pieChart" />
            <!-- 添加下载按钮 -->
            <div class="chart-actions">
              <el-dropdown @command="handleDownloadPieChart" split-button type="primary" size="small">
                <el-icon><Download /></el-icon>
                Download Pie Chart
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="png">PNG Format</el-dropdown-item>
                    <el-dropdown-item command="svg">SVG Format</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
          <!-- Analysis Results数值部分已移除 -->
        </div>
        
        <div class="summary-card">
          <h3>Overall Assessment</h3>
          <p>By comparing the uploaded protein sequences with reference databases, the most likely metabolic pathways for this microorganism are:</p>
          
          <!-- 甲烷代谢途径 -->
          <div class="most-likely-pathway">
            <h4 class="pathway-type-title">Methane Metabolism</h4>
            <template v-if="findTopMethanePathways.pathways.length > 1">
              <ul class="top-pathways-list">
                <li v-for="pathway in findTopMethanePathways.pathways" :key="pathway">
                  {{ translatePathwayName(pathway) }}
                </li>
              </ul>
              <p>Metabolic Pathway Completeness: <span class="similarity-score">{{ findTopMethanePathways.highScore }}% ~ {{ findTopMethanePathways.lowScore }}%</span></p>
            </template>
            <template v-else>
              <h4>{{ translatePathwayName(results.most_likely_methane_pathway) }}</h4>
              <p>Metabolic Pathway Completeness: <span class="similarity-score">{{ findTopMethanePathways.highScore }}% ~ {{ findTopMethanePathways.lowScore }}%</span></p>
            </template>
          </div>
          
          <!-- 硫代谢途径 -->
          <div class="most-likely-pathway">
            <h4 class="pathway-type-title">Sulfur Metabolism</h4>
            <template v-if="findTopSulfurPathways.pathways.length > 1">
              <ul class="top-pathways-list">
                <li v-for="pathway in findTopSulfurPathways.pathways" :key="pathway">
                  {{ translatePathwayName(pathway) }}
                </li>
              </ul>
              <p>Metabolic Pathway Completeness: <span class="similarity-score">{{ findTopSulfurPathways.highScore }}% ~ {{ findTopSulfurPathways.lowScore }}%</span></p>
            </template>
            <template v-else>
              <h4>{{ translatePathwayName(results.most_likely_sulfur_pathway) }}</h4>
              <p>Metabolic Pathway Completeness: <span class="similarity-score">{{ findTopSulfurPathways.highScore }}% ~ {{ findTopSulfurPathways.lowScore }}%</span></p>
            </template>
          </div>
          
          <!-- 氮代谢途径 -->
          <div class="most-likely-pathway">
            <h4 class="pathway-type-title">Nitrogen Metabolism</h4>
            <template v-if="findTopNitrogenPathways.pathways.length > 1">
              <ul class="top-pathways-list">
                <li v-for="pathway in findTopNitrogenPathways.pathways" :key="pathway">
                  {{ translatePathwayName(pathway) }}
                </li>
              </ul>
              <p>Metabolic Pathway Completeness: <span class="similarity-score">{{ findTopNitrogenPathways.highScore }}% ~ {{ findTopNitrogenPathways.lowScore }}%</span></p>
            </template>
            <template v-else>
              <h4>{{ translatePathwayName(results.most_likely_nitrogen_pathway) }}</h4>
              <p>Metabolic Pathway Completeness: <span class="similarity-score">{{ findTopNitrogenPathways.highScore }}% ~ {{ findTopNitrogenPathways.lowScore }}%</span></p>
            </template>
          </div>
        </div>
        
        <!-- 添加OGT预测结果卡片 -->
        <div v-if="results.tome_results" class="summary-card ogt-card">
          <h3>Optimal Growth Temperature Prediction</h3>
          <div class="ogt-result">
            <el-progress
              type="dashboard"
              :percentage="Math.round(results.tome_results.predOGT)"
              :color="getOgtColor(results.tome_results.predOGT)"
              :format="(val) => `${results.tome_results.predOGT.toFixed(1)}°C`"
              :width="120"
            />
            <div class="ogt-details">
              <div class="ogt-label">Optimal Growth Temperature</div>
              <div class="ogt-value">{{ results.tome_results.predOGT.toFixed(1) }}°C</div>
              <div class="ogt-category">{{ getTemperatureCategory(results.tome_results.predOGT) }}</div>
            </div>
          </div>
        </div>
        
        <!-- Salt Tolerance Card -->
        <div v-if="results.salt_tolerance" class="summary-card salt-tolerance-card">
          <h3>Salt Tolerance Assessment</h3>
          <div class="salt-tolerance-details">
            <div class="salt-level-display">
              <div class="salt-level-value" :class="getSaltToleranceClass(results.salt_tolerance.matched_sequences)">
                {{ results.salt_tolerance.tolerance_level || getSaltToleranceLevel(results.salt_tolerance.matched_sequences) }}
              </div>
            </div>
            <div class="salt-tolerance-info">
              <h4 class="levels-title">Salt Tolerance Levels</h4>
              <div class="salt-levels-container">
                <div class="salt-level-item very-strong-color" :class="{ active: results.salt_tolerance.matched_sequences >= 9 }">
                  <div class="level-name">Very Strong</div>
                </div>
                <div class="salt-level-item strong-color" :class="{ active: results.salt_tolerance.matched_sequences >= 6 && results.salt_tolerance.matched_sequences <= 8 }">
                  <div class="level-name">Strong</div>
                </div>
                <div class="salt-level-item moderate-color" :class="{ active: results.salt_tolerance.matched_sequences >= 3 && results.salt_tolerance.matched_sequences <= 5 }">
                  <div class="level-name">Moderate</div>
                </div>
                <div class="salt-level-item no-resistance-color" :class="{ active: results.salt_tolerance.matched_sequences >= 0 && results.salt_tolerance.matched_sequences <= 2 }">
                  <div class="level-name">No Resistance</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- CheckM2 Analysis Results -->
        <div v-if="results.checkm2_results" class="summary-card checkm2-card">
          <h3>Genome Quality Assessment</h3>
          <div class="checkm2-result">
            <div class="quality-metrics">
              <div class="quality-indicator">
                <h4>Completeness</h4>
                <el-progress
                  type="dashboard"
                  :percentage="Math.round(results.checkm2_results.completeness)"
                  :color="getCompletenessColor(results.checkm2_results.completeness)"
                  :width="80"
                />
                <p>{{ results.checkm2_results.completeness.toFixed(2) }}%</p>
              </div>
              
              <div class="quality-indicator">
                <h4>Contamination</h4>
                <el-progress
                  type="dashboard"
                  :percentage="Math.round(results.checkm2_results.contamination)"
                  :color="getContaminationColor(results.checkm2_results.contamination)"
                  :width="80"
                />
                <p>{{ results.checkm2_results.contamination.toFixed(2) }}%</p>
              </div>
            </div>
            
            <div class="quality-summary">
              <h4>Quality Assessment</h4>
              <el-tag :type="getQualityTagType(results.checkm2_results.quality_level)">
                {{ results.checkm2_results.quality_level }}
              </el-tag>
            </div>
            
            <!-- Cultivation Difficulty Assessment -->
            <div v-if="results.checkm2_results && (results.checkm2_results.cultivation_assessment || results.checkm2_results.cultivation_difficulty)" class="cultivation-assessment">
              <h4>Cultivation Assessment</h4>
              
              <!-- 新算法栽培评估结果 -->
              <template v-if="results.checkm2_results.cultivation_assessment">
                <el-tag :type="getCultivationTagType(results.checkm2_results.cultivation_assessment.cultivation_difficulty)">
                  {{ results.checkm2_results.cultivation_assessment.cultivation_difficulty }}
                </el-tag>
                <p class="cultivation-details">
                  Matching Genes: <strong>{{ results.checkm2_results.cultivation_assessment.matching_genes }}</strong> |
                  Total Genes: <strong>{{ results.checkm2_results.cultivation_assessment.total_genes }}</strong> |
                  Match Ratio: <strong>{{ results.checkm2_results.cultivation_assessment.match_ratio }}%</strong>
                </p>
                <p class="cultivation-criteria">
                  Criteria: 
                  <strong class="success-text">≤11%: Cultivable</strong> | 
                  <strong class="warning-text">11%-13%: Co-cultivation dependent</strong> | 
                  <strong class="danger-text">>13%: Obligate symbiont</strong>
                </p>
                <el-button 
                  v-if="results.checkm2_results.cultivation_assessment.result_file" 
                  type="primary" 
                  size="small" 
                  @click="downloadCultivationResults"
                >
                  Download Cultivation Assessment
                </el-button>
              </template>
              
              <!-- 旧算法栽培评估结果 (作为备用) -->
              <template v-else>
                <el-tag :type="getCultivationTagType(results.checkm2_results.cultivation_difficulty)">
                  {{ results.checkm2_results.cultivation_difficulty }}
                </el-tag>
                <p class="cultivation-details">
                  Gene Count: <strong>{{ results.checkm2_results.gene_count }}</strong> | 
                  Ratio (Gene Count/Completeness): <strong>{{ results.checkm2_results.cultivation_ratio }}</strong>
                </p>
              </template>
            </div>
            
            <el-button 
              v-if="results.checkm2_results.result_file" 
              type="primary" 
              size="small" 
              @click="downloadCheckM2Results"
            >
              Download CheckM2 Report
            </el-button>
          </div>
        </div>
        
        <!-- Table showing detailed results -->
        <h3>Comparison Details for Each Metabolic Pathway</h3>
        <el-tabs type="border-card" v-model="activeTab" @tab-click="handleTabChange">
          <el-tab-pane label="Methane Metabolism" name="methane">
            <el-skeleton :loading="tableLoading" animated :rows="5" v-if="tableLoading">
            </el-skeleton>
            <el-table
              v-else
              :data="getTranslatedData(results.methane_summary)"
              style="width: 100%"
              :default-sort="{prop: 'completeness', order: 'descending'}"
              border
              stripe
              v-loading="tableLoading"
            >
              <el-table-column prop="pathway_name" label="Metabolic Pathway" min-width="180" />
              <el-table-column prop="matching_sequences" label="Matching Sequences" min-width="120" sortable />
              <el-table-column prop="average_identity" label="Average Identity %" min-width="120" sortable>
                <template #default="scope">
                  {{ scope.row.average_identity }}%
                </template>
              </el-table-column>
              <el-table-column prop="high_quality_match" label="High-Quality Match %" min-width="150" sortable>
                <template #default="scope">
                  {{ scope.row.high_quality_match }}%
                </template>
              </el-table-column>
              <el-table-column prop="completeness" label="Metabolic Pathway Completeness %" min-width="250" sortable>
                <template #default="scope">
                  <span :class="{'highlight-score': scope.row.completeness === 100}">
                    {{ scope.row.high_standard_completeness }}% ~ {{ scope.row.low_standard_completeness }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" min-width="100" fixed="right">
                <template #default="scope">
                  <el-button
                    size="small"
                    type="primary"
                    @click="downloadResult(scope.row.database)"
                  >
                    Download
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
          
          <el-tab-pane label="Sulfur Metabolism" name="sulfur">
            <el-skeleton :loading="tableLoading" animated :rows="5" v-if="tableLoading">
            </el-skeleton>
            <el-table
              v-else
              :data="getTranslatedData(results.sulfur_summary)"
              style="width: 100%"
              :default-sort="{prop: 'completeness', order: 'descending'}"
              border
              stripe
              v-loading="tableLoading"
            >
              <el-table-column prop="pathway_name" label="Metabolic Pathway" min-width="180" />
              <el-table-column prop="matching_sequences" label="Matching Sequences" min-width="120" sortable />
              <el-table-column prop="average_identity" label="Average Identity %" min-width="120" sortable>
                <template #default="scope">
                  {{ scope.row.average_identity }}%
                </template>
              </el-table-column>
              <el-table-column prop="high_quality_match" label="High-Quality Match %" min-width="150" sortable>
                <template #default="scope">
                  {{ scope.row.high_quality_match }}%
                </template>
              </el-table-column>
              <el-table-column prop="completeness" label="Metabolic Pathway Completeness %" min-width="250" sortable>
                <template #default="scope">
                  <span :class="{'highlight-score': scope.row.completeness === 100}">
                    {{ scope.row.high_standard_completeness }}% ~ {{ scope.row.low_standard_completeness }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" min-width="100" fixed="right">
                <template #default="scope">
                  <el-button
                    size="small"
                    type="primary"
                    @click="downloadResult(scope.row.database)"
                  >
                    Download
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
          
          <el-tab-pane label="Nitrogen Metabolism" name="nitrogen">
            <el-skeleton :loading="tableLoading" animated :rows="5" v-if="tableLoading">
            </el-skeleton>
            <el-table
              v-else
              :data="getTranslatedData(results.nitrogen_summary)"
              style="width: 100%"
              :default-sort="{prop: 'completeness', order: 'descending'}"
              border
              stripe
              v-loading="tableLoading"
            >
              <el-table-column prop="pathway_name" label="Metabolic Pathway" min-width="180" />
              <el-table-column prop="matching_sequences" label="Matching Sequences" min-width="120" sortable />
              <el-table-column prop="average_identity" label="Average Identity %" min-width="120" sortable>
                <template #default="scope">
                  {{ scope.row.average_identity }}%
                </template>
              </el-table-column>
              <el-table-column prop="high_quality_match" label="High-Quality Match %" min-width="150" sortable>
                <template #default="scope">
                  {{ scope.row.high_quality_match }}%
                </template>
              </el-table-column>
              <el-table-column prop="completeness" label="Metabolic Pathway Completeness %" min-width="250" sortable>
                <template #default="scope">
                  <span :class="{'highlight-score': scope.row.completeness === 100}">
                    {{ scope.row.high_standard_completeness }}% ~ {{ scope.row.low_standard_completeness }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" min-width="100" fixed="right">
                <template #default="scope">
                  <el-button
                    size="small"
                    type="primary"
                    @click="downloadResult(scope.row.database)"
                  >
                    Download
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
          
          <el-tab-pane label="All Pathways" name="all">
            <el-skeleton :loading="tableLoading" animated :rows="5" v-if="tableLoading">
            </el-skeleton>
            <el-table
              v-else
              :data="getTranslatedData(results.summary)"
              style="width: 100%"
              :default-sort="{prop: 'completeness', order: 'descending'}"
              border
              stripe
              v-loading="tableLoading"
            >
              <el-table-column prop="pathway_name" label="Metabolic Pathway" min-width="180" />
              <el-table-column prop="metabolism_type" label="Metabolism Type" min-width="120">
                <template #default="scope">
                  {{ getMetabolismTypeLabel(scope.row.metabolism_type) }}
                </template>
              </el-table-column>
              <el-table-column prop="matching_sequences" label="Matching Sequences" min-width="120" sortable />
              <el-table-column prop="average_identity" label="Average Identity %" min-width="120" sortable>
                <template #default="scope">
                  {{ scope.row.average_identity }}%
                </template>
              </el-table-column>
              <el-table-column prop="high_quality_match" label="High-Quality Match %" min-width="150" sortable>
                <template #default="scope">
                  {{ scope.row.high_quality_match }}%
                </template>
              </el-table-column>
              <el-table-column prop="completeness" label="Metabolic Pathway Completeness %" min-width="250" sortable>
                <template #default="scope">
                  <span :class="{'highlight-score': scope.row.completeness === 100}">
                    {{ scope.row.high_standard_completeness }}% ~ {{ scope.row.low_standard_completeness }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" min-width="100" fixed="right">
                <template #default="scope">
                  <el-button
                    size="small"
                    type="primary"
                    @click="downloadResult(scope.row.database)"
                  >
                    Download
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
        
        <div class="download-section">
          <el-button 
            type="success" 
            @click="downloadSummary"
            class="download-summary-button"
          >
            Download Comprehensive Results
          </el-button>
          <el-button 
            type="info" 
            @click="downloadSummaryText"
            class="download-summary-button"
            style="margin-left: 10px"
          >
            Download Summary Text
          </el-button>
        </div>
        
        <div class="explanation-section">
          <h3>Analysis Metrics Explanation</h3>
          <ul>
            <li><strong>Matching Sequences:</strong> Number of sequences in the query that match the reference database</li>
            <li><strong>Average Identity %:</strong> Average similarity percentage of matching sequences</li>
            <li><strong>High-Quality Match %:</strong> Percentage of matches with identity greater than 60%</li>
            <li><strong>Metabolic Pathway Completeness %:</strong> Displayed as a range (High~Low standard) showing percentage of matching reference sequences relative to the total number in the database:
              <ul>
                <li><em>High standard:</em> Using strict criteria (E-value &lt; 1e-100, bitscore &gt; 400)</li>
                <li><em>Low standard:</em> Using relaxed criteria (E-value &lt; 1e-5, bitscore &gt; 40)</li>
              </ul>
            </li>
          </ul>
          <p><small>Note: The metabolic pathway completeness is shown as a range from high standard to low standard match results. This provides both a conservative estimate and a more inclusive assessment of the metabolic capabilities.</small></p>
          <p><small>For cultivation assessment: E-value &lt; 1e-3 and bitscore &gt; 50</small></p>
        </div>
      </div>

      <!-- Citation section -->
      <div class="citation-section">
        <h3>Citation Information</h3>
        <div class="citation-content">
          <p class="citation-text">
            This tool integrates Diamond, developed by Buchfink et al. If you use this tool in your research, please cite the following reference:
          </p>
          <div class="citation-box">
            <p class="paper-citation">
              <strong>Primary Citation:</strong><br>
              Buchfink B., Xie C., Huson D.H. (2015). Fast and sensitive protein alignment using DIAMOND.
              <em>Nature Methods</em>, 12, 59–60.
              <a href="https://doi.org/10.1038/nmeth.3176" target="_blank">https://doi.org/10.1038/nmeth.3176</a>
            </p>
            <p class="repo-citation">
              <strong>Software Repository:</strong><br>
              Diamond GitHub Repository: <a href="https://github.com/bbuchfink/diamond" target="_blank">https://github.com/bbuchfink/diamond</a>
            </p>
          </div>
          <div class="license-info">
            <p class="license-text">
              Diamond is licensed under the GNU General Public License v3.0 (GPL-3.0).
            </p>
            <p class="usage-note">
              This tool is an integrated implementation of Diamond, not an official product of the Diamond project. Please visit the Diamond GitHub repository for the latest updates and official implementation.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import { UploadFilled, Download } from '@element-plus/icons-vue'
import { ElMessage, ElProgress } from 'element-plus'
import axios from 'axios'
import { use } from 'echarts/core'
import * as echarts from 'echarts'
import { RadarChart, PieChart, BarChart } from 'echarts/charts'
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers'
import { 
  LegendComponent, 
  TooltipComponent, 
  GridComponent,
  TitleComponent,
  ToolboxComponent,
  PolarComponent,
  // 移除 AngleAxisComponent，因为它不存在
  SingleAxisComponent 
} from 'echarts/components'
import VChart from 'vue-echarts'

// 注册ECharts组件
use([
  RadarChart,
  PieChart,
  BarChart,
  CanvasRenderer,
  SVGRenderer,
  LegendComponent,
  TooltipComponent,
  GridComponent,
  TitleComponent,
  ToolboxComponent,
  PolarComponent,
  // 移除 AngleAxisComponent
  SingleAxisComponent
])

// Status variables
const uploading = ref(false)
const uploadProgress = ref(0)
const progressText = ref('')
const jobId = ref(null)
const showAnalysisOptions = ref(false)
const analyzing = ref(false)
const results = ref({})
const activeTab = ref('methane')
const tableLoading = ref(false)
const radarChart = ref(null) // 雷达图实例引用
const pieChart = ref(null) // 饼状图实例引用
const polarChart = ref(null) // 极坐标柱状图实例引用

// Computed properties
const canAnalyze = computed(() => {
  return jobId.value && !analyzing.value
})

// File upload validation
const beforeUpload = (file) => {
  // Check file type
  const validTypes = ['.faa', '.fasta', '.fa']
  const fileName = file.name.toLowerCase()
  const isValidType = validTypes.some(type => fileName.endsWith(type))
  
  if (!isValidType) {
    ElMessage.error('Please upload a protein sequence file in .faa, .fasta, or .fa format')
    return false
  }
  
  // Check file size (max 50MB)
  const isLessThan50MB = file.size / 1024 / 1024 < 50
  if (!isLessThan50MB) {
    ElMessage.error('File size cannot exceed 50MB')
    return false
  }
  
  return true
}

// Handle upload progress
const handleProgress = (event) => {
  uploading.value = true
  uploadProgress.value = Math.round(event.percent)
  progressText.value = uploadProgress.value === 100 
    ? 'File upload complete, preparing for analysis...' 
    : `Uploading file: ${uploadProgress.value}%`
}

// Format progress display
const progressFormat = (percentage) => {
  return percentage === 100 ? 'Complete' : `${percentage}%`
}

// Handle successful upload
const handleSuccess = (response) => {
  uploading.value = false
  
  if (response.status === 'success') {
    jobId.value = response.job_id
    showAnalysisOptions.value = true
    ElMessage.success('File uploaded successfully')
  } else {
    ElMessage.error(`Upload failed: ${response.message || 'Unknown error'}`)
  }
}

// Handle upload error
const handleError = (error) => {
  uploading.value = false
  console.error('Upload error:', error)
  ElMessage.error('File upload failed, please try again')
}

// Start analysis
const startAnalysis = async () => {
  if (!jobId.value) {
    ElMessage.warning('Please upload a file first')
    return
  }
  
  analyzing.value = true
  progressText.value = 'Analyzing protein sequences...'
  
  try {
    // Call Diamond analysis API with Tome analysis
    const response = await axios.post('/api/tools/diamond/analyze/', {
      job_id: jobId.value,
      run_tome: true  // Add parameter to run Tome analysis
    })
    
    if (response.data.status === 'success') {
      results.value = response.data.results
      
      // 添加调试输出
      console.log("Analysis Results:", response.data.results)
      if (response.data.results.checkm2_results) {
        console.log("CheckM2结果:", response.data.results.checkm2_results)
        if (response.data.results.checkm2_results.cultivation_assessment) {
          console.log("培养评估结果:", response.data.results.checkm2_results.cultivation_assessment)
        }
      }
      
      ElMessage.success('Analysis complete')
    } else {
      ElMessage.error(`Analysis failed: ${response.data.message || 'Unknown error'}`)
    }
  } catch (error) {
    console.error('Analysis error:', error)
    ElMessage.error('Error during analysis, please try again')
  } finally {
    analyzing.value = false
    progressText.value = ''
  }
}

// Download specific database alignment results
const downloadResult = (dbName) => {
  if (!jobId.value) return
  
  const url = `/api/tools/diamond/download/${jobId.value}/${dbName}_results.tsv`
  window.open(url, '_blank')
}

// Download comprehensive analysis results
const downloadSummary = () => {
  if (!jobId.value) return
  
  const url = `/api/tools/diamond/download/${jobId.value}/similarity_summary.csv`
  window.open(url, '_blank')
}

// Download summary text file containing all analysis results
const downloadSummaryText = () => {
  if (!jobId.value) return
  
  const url = `/api/tools/diamond/download/${jobId.value}/comprehensive_summary.txt`
  window.open(url, '_blank')
}

// Translate pathway names from Chinese to English
const translatePathwayName = (chineseName) => {
  const translations = {
    // 甲烷代谢途径
    '乙酸产甲烷途径': 'Acetate Methanogenesis',
    'CO2还原产甲烷途径': 'Carbon dioxide Methanogenesis',
    '甲醇产甲烷途径': 'Methanol Methanogenesis',
    '甲胺产甲烷途径': 'Methylamine Methanogenesis',
    '甲硫醇产甲烷途径': 'Methanethiol Methanogenesis',
    // 新增七个产甲烷代谢途径
    '碳16产甲烷途径': 'Long-chain alkyl hydrocarbons Methanogenesis',
    '一氧化碳产甲烷途径': 'Carbon Monoxide Methanogenesis',
    '甲酸产甲烷途径': 'Formate Methanogenesis',
    '甲氧基产甲烷途径': 'Methoxy Methanogenesis',
    '脂肪酸产甲烷途径': 'Fatty Acid Methanogenesis',
    '二甲胺产甲烷途径': 'Dimethylamine Methanogenesis',
    '三甲胺产甲烷途径': 'Trimethylamine Methanogenesis',
    
    // 硫代谢途径
    '同化型硫酸盐还原途径': 'Assimilatory Sulfate Reduction',
    '硫化物氧化途径': 'Sulfide Oxidation',
    '硫氧化SOX系统途径': 'Sulfur Oxidation (SOX System)',
    '硫氧化四硫酸盐中间体途径': 'Sulfur Oxidation (Tetrathionate Intermediate Pathway)',
    '硫还原途径': 'Sulfur Reduction',
    
    // 氮代谢途径
    '同化型硝酸盐还原途径': 'Assimilatory Nitrate Reduction',
    '反硝化途径': 'Denitrification',
    '异化型硝酸盐还原途径': 'Dissimilatory Nitrate Reduction',
    '硝化途径': 'Nitrification',
    
    // 耐盐特性
    '耐盐特性': 'Salt Tolerance',
    
    // 栽培评估
    '栽培评估': 'Cultivation Assessment'
  }
  
  return translations[chineseName] || chineseName
}

// Transform Chinese data to English for tables
const getTranslatedData = (data) => {
  if (!data || !data.length) return []
  
  return data.map(item => {
    return {
      pathway_name: translatePathwayName(item['途径名称']),
      matching_sequences: item['匹配序列数'],
      average_identity: item['平均一致性%'],
      high_quality_match: item['高质量匹配比例%'],
      completeness: item['代谢途径完整度%'],
      high_standard_completeness: item['高标准完整度%'],
      low_standard_completeness: item['低标准完整度%'],
      database: item['数据库'],
      metabolism_type: item['代谢类型'] || 'other'
    }
  })
}

// Get metabolism type label
const getMetabolismTypeLabel = (type) => {
  const types = {
    'methane': 'Methane Metabolism',
    'sulfur': 'Sulfur Metabolism',
    'nitrogen': 'Nitrogen Metabolism',
    'other': 'Other'
  }
  return types[type] || type
}

// Get salt tolerance score class based on matched sequences count
const getSaltToleranceClass = (matched_sequences) => {
  if (matched_sequences >= 8) {
    return 'extreme-color'
  } else if (matched_sequences >= 5) {
    return 'very-strong-color'
  } else if (matched_sequences >= 2) {
    return 'strong-color'
  } else if (matched_sequences >= 1) {
    return 'moderate-color'
  } else {
    return 'no-resistance-color'
  }
}

// Get salt tolerance level description based on matched sequences count
const getSaltToleranceLevel = (matched_sequences) => {
  if (matched_sequences >= 9) {
    return 'Very Strong Salt tolerance'
  } else if (matched_sequences >= 6) {
    return 'Strong Salt tolerance'
  } else if (matched_sequences >= 3) {
    return 'Moderate Salt tolerance'
  } else {
    return 'No Resistance'
  }
}

// Download CheckM2 results
const downloadCheckM2Results = () => {
  if (!results.value.checkm2_results || !results.value.checkm2_results.result_file) return
  
  const url = `/api/media/${results.value.checkm2_results.result_file}`
  window.open(url, '_blank')
}

// Download Cultivation Assessment results
const downloadCultivationResults = () => {
  if (!results.value.checkm2_results || 
      !results.value.checkm2_results.cultivation_assessment || 
      !results.value.checkm2_results.cultivation_assessment.result_file) return
  
  const url = `/api/media/${results.value.checkm2_results.cultivation_assessment.result_file}`
  window.open(url, '_blank')
}

// Find pathways with the highest completeness score for methane metabolism
const findTopMethanePathways = computed(() => {
  if (!results.value.methane_summary || !results.value.methane_summary.length) {
    return { pathways: [], score: 0, highScore: 0, lowScore: 0 };
  }
  
  // 优先查找低标准完整度为100%的途径
  const perfectLowScorePathways = results.value.methane_summary.filter(item => item['低标准完整度%'] === 100);
  
  if (perfectLowScorePathways.length > 0) {
    // 如果有低标准100%的结果，优先使用这些结果
    const topPathways = perfectLowScorePathways.map(item => item['途径名称']);
    const pathway = perfectLowScorePathways[0]; // 取第一个100%的途径
    
    return {
      pathways: topPathways,
      score: pathway['代谢途径完整度%'],
      highScore: pathway['高标准完整度%'],
      lowScore: 100 // 确保使用100分
    };
  }
  
  // 如果没有低标准100%的结果，使用原来的逻辑
  // 找出高标准和低标准的最高分
  const highScores = results.value.methane_summary.map(item => item['高标准完整度%']);
  const lowScores = results.value.methane_summary.map(item => item['低标准完整度%']);
  const maxHighScore = Math.max(...highScores);
  const maxLowScore = Math.max(...lowScores);
  
  // 找出代谢途径完整度最高的那些途径
  const scores = results.value.methane_summary.map(item => item['代谢途径完整度%']);
  const maxScore = Math.max(...scores);
  
  // Get all pathways with this max score
  const topPathways = results.value.methane_summary
    .filter(item => item['代谢途径完整度%'] === maxScore)
    .map(item => item['途径名称']);
  
  // 找到这些途径中的高标准和低标准分数
  const pathway = results.value.methane_summary.find(item => item['代谢途径完整度%'] === maxScore);
  const highScore = pathway ? pathway['高标准完整度%'] : maxHighScore;
  const lowScore = pathway ? pathway['低标准完整度%'] : maxLowScore;
  
  return {
    pathways: topPathways,
    score: maxScore,
    highScore: highScore,
    lowScore: lowScore
  };
})

// Find pathways with the highest completeness score for sulfur metabolism
const findTopSulfurPathways = computed(() => {
  if (!results.value.sulfur_summary || !results.value.sulfur_summary.length) {
    return { pathways: [], score: 0, highScore: 0, lowScore: 0 };
  }
  
  // 找出高标准和低标准的最高分
  const highScores = results.value.sulfur_summary.map(item => item['高标准完整度%']);
  const lowScores = results.value.sulfur_summary.map(item => item['低标准完整度%']);
  const maxHighScore = Math.max(...highScores);
  const maxLowScore = Math.max(...lowScores);
  
  // 找出代谢途径完整度最高的那些途径
  const scores = results.value.sulfur_summary.map(item => item['代谢途径完整度%']);
  const maxScore = Math.max(...scores);
  
  // Get all pathways with this max score
  const topPathways = results.value.sulfur_summary
    .filter(item => item['代谢途径完整度%'] === maxScore)
    .map(item => item['途径名称']);
  
  // 找到这些途径中的高标准和低标准分数
  const pathway = results.value.sulfur_summary.find(item => item['代谢途径完整度%'] === maxScore);
  const highScore = pathway ? pathway['高标准完整度%'] : maxHighScore;
  const lowScore = pathway ? pathway['低标准完整度%'] : maxLowScore;
  
  return {
    pathways: topPathways,
    score: maxScore,
    highScore: highScore,
    lowScore: lowScore
  };
})

// Find pathways with the highest completeness score for nitrogen metabolism
const findTopNitrogenPathways = computed(() => {
  if (!results.value.nitrogen_summary || !results.value.nitrogen_summary.length) {
    return { pathways: [], score: 0, highScore: 0, lowScore: 0 };
  }
  
  // 找出高标准和低标准的最高分
  const highScores = results.value.nitrogen_summary.map(item => item['高标准完整度%']);
  const lowScores = results.value.nitrogen_summary.map(item => item['低标准完整度%']);
  const maxHighScore = Math.max(...highScores);
  const maxLowScore = Math.max(...lowScores);
  
  // 找出代谢途径完整度最高的那些途径
  const scores = results.value.nitrogen_summary.map(item => item['代谢途径完整度%']);
  const maxScore = Math.max(...scores);
  
  // Get all pathways with this max score
  const topPathways = results.value.nitrogen_summary
    .filter(item => item['代谢途径完整度%'] === maxScore)
    .map(item => item['途径名称']);
  
  // 找到这些途径中的高标准和低标准分数
  const pathway = results.value.nitrogen_summary.find(item => item['代谢途径完整度%'] === maxScore);
  const highScore = pathway ? pathway['高标准完整度%'] : maxHighScore;
  const lowScore = pathway ? pathway['低标准完整度%'] : maxLowScore;
  
  return {
    pathways: topPathways,
    score: maxScore,
    highScore: highScore,
    lowScore: lowScore
  };
})

// CheckM2 analysis related methods
const getCompletenessColor = (completeness) => {
  if (completeness >= 90) return '#67C23A'
  if (completeness >= 70) return '#E6A23C'
  return '#F56C6C'
}

const getContaminationColor = (contamination) => {
  if (contamination <= 5) return '#67C23A'
  if (contamination <= 10) return '#E6A23C'
  return '#F56C6C'
}

const getQualityTagType = (quality) => {
  if (quality === 'High Quality') return 'success'
  if (quality === 'Medium Quality') return 'warning'
  return 'danger'
}

// Get cultivation difficulty tag type
const getCultivationTagType = (difficulty) => {
  if (difficulty.includes('Cultivable') && !difficulty.includes('Obligate symbiont')) return 'success'
  if (difficulty.includes('Co-cultivation dependent')) return 'warning'
  if (difficulty.includes('Obligate symbiont')) return 'danger'
  return 'info'
}

// Handle tab change
// eslint-disable-next-line no-unused-vars
const handleTabChange = (_tab) => {
  tableLoading.value = true
  
  // Use nextTick and setTimeout to ensure DOM has time to update
  nextTick(() => {
    setTimeout(() => {
      tableLoading.value = false
    }, 100)
  })
}

// Get OGT temperature color
const getOgtColor = (temperature) => {
  if (temperature >= 65) return '#F56C6C' // Hyperthermophile
  if (temperature >= 45) return '#E6A23C' // Thermophile
  if (temperature >= 20) return '#67C23A' // Mesophile
  return '#409EFF' // Psychrophile
}

// Get temperature category
const getTemperatureCategory = (temperature) => {
  if (temperature >= 65) return 'Hyperthermophile'
  if (temperature >= 45) return 'Thermophile'
  if (temperature >= 20) return 'Mesophile'
  if (temperature >= 5) return 'Psychrophile'
  return 'Hypopsychrophile'
}

// 获取途径颜色 - 根据代谢途径名称分配固定颜色，并区分高低标准完整度
const getPathwayColor = (pathwayName, isHighStandard = true) => {
  // 为每个途径定义固定的色系
  const pathwayColorMap = {
    // 甲烷代谢途径 - 每个途径都有固定的颜色
    'CO2 Reduction Methanogenesis': ['#4169E1', '#4169E1', '#4169E1', '#B0C4DE', '#B0C4DE'], // 皇家蓝/淡钢蓝
    'Methylamine Methanogenesis': ['#CD5C5C', '#CD5C5C', '#CD5C5C', '#F08080', '#F08080'], // 印度红/浅珊瑚色
    'Methanol Methanogenesis': ['#2E8B57', '#2E8B57', '#2E8B57', '#8FBC8F', '#8FBC8F'], // 海洋绿/暗海洋绿
    'Methanethiol Methanogenesis': ['#9370DB', '#9370DB', '#9370DB', '#D8BFD8', '#D8BFD8'], // 中紫色/蓟色
    'Acetoclastic Methanogenesis': ['#F4A460', '#F4A460', '#F4A460', '#FFDAB9', '#FFDAB9'], // 沙褐色/桃色
    'Carbon-16 Methanogenesis': ['#696969', '#696969', '#696969', '#D3D3D3', '#D3D3D3'], // 暗灰色/浅灰色
    'Carbon Monoxide Methanogenesis': ['#8A2BE2', '#8A2BE2', '#8A2BE2', '#BDA0CB', '#BDA0CB'], // 紫罗兰色/中等紫色
    'Formate Methanogenesis': ['#DAA520', '#DAA520', '#DAA520', '#F0E68C', '#F0E68C'], // 金菊色/卡其色
    'Methoxy Methanogenesis': ['#5F9EA0', '#5F9EA0', '#5F9EA0', '#AFEEEE', '#AFEEEE'], // 军校蓝/淡绿松石色
    'Fatty Acid Methanogenesis': ['#A0522D', '#A0522D', '#A0522D', '#DEB887', '#DEB887'], // 赭色/硬木色
    'Dimethylamine Methanogenesis': ['#4682B4', '#4682B4', '#4682B4', '#ADD8E6', '#ADD8E6'], // 钢蓝色/淡蓝色
    'Trimethylamine Methanogenesis': ['#3CB371', '#3CB371', '#3CB371', '#90EE90', '#90EE90'], // 中海绿色/淡绿色
    
    // 硫代谢途径
    'Assimilatory Sulfate Reduction': ['#FF7F50', '#FF7F50', '#FF7F50', '#FFA07A', '#FFA07A'], // 珊瑚色/浅鲑鱼色
    'Sulfide Oxidation': ['#6A5ACD', '#6A5ACD', '#6A5ACD', '#7B68EE', '#7B68EE'], // 板岩蓝/中板岩蓝
    'Sulfur Oxidation (SOX System)': ['#20B2AA', '#20B2AA', '#20B2AA', '#66CDAA', '#66CDAA'], // 浅海绿/中绿宝石
    'Sulfur Oxidation (Tetrathionate Intermediate Pathway)': ['#FF6347', '#FF6347', '#FF6347', '#FF7F50', '#FF7F50'], // 番茄色/珊瑚色
    'Sulfur Reduction': ['#BA55D3', '#BA55D3', '#BA55D3', '#DA70D6', '#DA70D6'], // 中兰花紫/兰花色
    
    // 氮代谢途径
    'Assimilatory Nitrate Reduction': ['#32CD32', '#32CD32', '#32CD32', '#90EE90', '#90EE90'], // 酸橙绿/淡绿
    'Denitrification': ['#FF4500', '#FF4500', '#FF4500', '#FF7F50', '#FF7F50'], // 橙红色/珊瑚色
    'Dissimilatory Nitrate Reduction': ['#1E90FF', '#1E90FF', '#1E90FF', '#87CEFA', '#87CEFA'], // 道奇蓝/淡天蓝
    'Nitrification': ['#FFD700', '#FFD700', '#FFD700', '#FFEC8B', '#FFEC8B'], // 金色/浅黄
    
    // 默认颜色 - 如果没有匹配到特定途径
    'default': ['#808080', '#808080', '#808080', '#D3D3D3', '#D3D3D3'] // 灰色/浅灰色
  };
  
  // 调试输出
  console.log('寻找颜色映射:', pathwayName);
  
  // 获取途径对应的颜色数组，如果没有找到则使用默认颜色
  const colorArray = pathwayColorMap[pathwayName] || pathwayColorMap['default'];
  
  // 选择颜色 - 高标准使用深色(0)，低标准使用浅色(3)
  const colorIndex = isHighStandard ? 0 : 3;
  
  // 返回对应途径和标准的颜色
  return colorArray[colorIndex];
}

// 添加极坐标柱状图选项 - 显示所有甲烷代谢途径及其高低标准完整度
const polarChartOptions = computed(() => {
  if (!results.value || !results.value.methane_summary || !results.value.methane_summary.length) return {}
  
  // 获取所有甲烷代谢途径数据，而不仅仅是最高分的
  const methaneSummary = results.value.methane_summary
  
  // 准备数据
  const highStandardData = []
  const lowStandardData = []
  const categories = []
  
  // 遍历所有途径数据
  methaneSummary.forEach((pathway) => {
    // 获取英文途径名称
    const pathwayName = translatePathwayName(pathway['途径名称'])
    // 添加类别
    categories.push(pathwayName)
    
    // 添加高标准数据点
    highStandardData.push({
      value: pathway['高标准完整度%'],
      name: pathwayName + ' (High Standard)',
      itemStyle: {
        color: getPathwayColor(pathwayName, true) // 根据途径名称分配固定颜色，高标准使用深色
      }
    })
    
    // 添加低标准数据点
    lowStandardData.push({
      value: pathway['低标准完整度%'],
      name: pathwayName + ' (Low Standard)',
      itemStyle: {
        color: getPathwayColor(pathwayName, false) // 根据途径名称分配固定颜色，低标准使用浅色
      }
    })
  })
  
  return {
    title: {
      text: 'Methane Pathway Completeness',
      left: 'center',
      textStyle: {
        fontFamily: 'Arial',
        fontSize: 16,
        color: '#000000'
      }
    },
    legend: {
      data: [
        {
          name: 'High Standard',
          itemStyle: {
            color: '#000000' // 深黑色
          }
        },
        {
          name: 'Low Standard',
          itemStyle: {
            color: '#D3D3D3' // 浅灰色
          }
        }
      ],
      bottom: 0,
      itemWidth: 15,
      itemHeight: 10,
      textStyle: {
        fontFamily: 'Arial',
        fontSize: 12,
        color: '#000000'
      }
    },
    polar: {
      radius: '65%'
    },
    angleAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        show: false // 隐藏圆圈外围的代谢途径名称文字
      },
      axisTick: {
        alignWithLabel: true
      }
    },
    radiusAxis: {
      name: 'Completeness (%)',
      nameLocation: 'end',
      max: 100
    },
    series: [
      {
        name: 'High Standard',
        type: 'bar',
        data: highStandardData,
        coordinateSystem: 'polar',
        itemStyle: {
          color: '#000000' // 深黑色作为高阈值（直系同源蛋白）
        },
        label: {
          show: false // 隐藏高标准扇形图中的百分比文字
        },
        emphasis: {
          focus: 'series'
        },
        z: 10 // 确保高标准在前面显示
      },
      {
        name: 'Low Standard',
        type: 'bar',
        data: lowStandardData,
        coordinateSystem: 'polar',
        itemStyle: {
          color: '#D3D3D3' // 浅灰色作为低阈值（旁系同源蛋白）
        },
        label: {
          show: false // 不显示低标准的标签，避免重叠
        },
        emphasis: {
          focus: 'series'
        },
        z: 5 // 低标准在后面显示
      }
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params) => {
        // 获取当前悬停的途径名称（去掉括号及其后面的内容）
        const pathwayName = params[0].name.split(' (')[0];
        
        // 查找高标准和低标准的数据
        let highStandardValue = null;
        let lowStandardValue = null;
        
        // 遍历所有参数，找到高标准和低标准的值
        params.forEach(param => {
          if (param.seriesName === 'High Standard') {
            highStandardValue = param.value;
          } else if (param.seriesName === 'Low Standard') {
            lowStandardValue = param.value;
          }
        });
        
        // 如果只找到一个值（高标准或低标准），则需要在数据中查找另一个值
        if (highStandardValue === null || lowStandardValue === null) {
          // 找到当前途径在categories中的索引
          const index = categories.findIndex(cat => cat === pathwayName);
          
          // 如果找到了索引，则获取对应的高标准和低标准值
          if (index !== -1) {
            if (highStandardValue === null) {
              highStandardValue = highStandardData[index].value;
            }
            if (lowStandardValue === null) {
              lowStandardValue = lowStandardData[index].value;
            }
          }
        }
        
        // 返回包含高标准和低标准的提示信息
        return `${pathwayName}<br/>High Standard: ${highStandardValue}%<br/>Low Standard: ${lowStandardValue}%`;
      }
    }
  }
})

// 添加饼状图选项
const pieChartOptions = computed(() => {
  if (!results.value || !results.value.checkm2_results) {
    return {
      series: []
    }
  }

  const completeness = Number(results.value.checkm2_results.completeness) || 0
  const contamination = Number(results.value.checkm2_results.contamination) || 0
  // 计算缺失部分的百分比（100% - 完整度 - 污染度）
  const missing = Math.max(0, 100 - completeness - contamination).toFixed(2)

  return {
    title: {
      text: 'Genome Quality',
      left: 'center',
      top: 0,
      textStyle: {
        fontFamily: 'Arial',
        fontSize: 16,
        color: '#000000'
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c}%'
    },
    legend: {
      orient: 'horizontal',
      bottom: 0,
      left: 'center',
      data: ['Completeness', 'Contamination', 'Missing'],
      itemWidth: 15,
      itemHeight: 10,
      textStyle: {
        fontFamily: 'Arial',
        fontSize: 12,
        color: '#000000'
      },
      itemStyle: {
        borderWidth: 0.3 // 设置图例边框更细
      }
    },
    series: [
      {
        name: 'Genome Quality',
        type: 'pie',
        radius: '65%',
        center: ['50%', '48%'],
        data: [
          { 
            value: completeness, 
            name: 'Completeness',
            itemStyle: {
              color: '#a8e6a1', // 柔和的淡绿色
              borderColor: '#000',
              borderWidth: 0.5
            }
          },
          { 
            value: contamination, 
            name: 'Contamination',
            itemStyle: {
              color: '#ffb1b1', // 柔和的淡红色
              borderColor: '#000',
              borderWidth: 0.5
            }
          },
          {
            value: missing,
            name: 'Missing',
            itemStyle: {
              color: '#d9d9d9', // 灰色
              borderColor: '#000',
              borderWidth: 0.5
            }
          }
        ],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        label: {
          show: true,
          formatter: '{b}: {c}%'
        }
      }
    ]
  }
})

// 添加雷达图选项
const radarChartOptions = computed(() => {
  if (!results.value || Object.keys(results.value).length === 0) {
    return {
      radar: {
        indicator: []
      },
      series: []
    }
  }

  // 设置雷达图的指标
  const indicators = [
    { name: 'Methane Metabolism', max: 100 },
    { name: 'Sulfur Metabolism', max: 100 },
    { name: 'Nitrogen Metabolism', max: 100 },
    { name: 'Optimal Growth Temperature', max: 100 },
    { name: 'Salt Tolerance', max: 100 },
    { name: 'Genome Completeness', max: 100 },
    { name: 'Genome Contamination', max: 100 },
    { name: 'Cultivation Assessment', max: 100 }
  ]

  // 计算各指标的值
  const values = [
    // 甲烷代谢 - 使用低标准完整度值(上限值)
    Number(findTopMethanePathways.value.lowScore || 0),
    
    // 硫代谢 - 使用低标准完整度值(上限值)
    Number(findTopSulfurPathways.value.lowScore || 0),
    
    // 氮代谢 - 使用低标准完整度值(上限值)
    Number(findTopNitrogenPathways.value.lowScore || 0),
    
    // OGT预测 - 转换为0-100的范围 (0℃->0%, 100℃->100%)
    results.value.tome_results ? Number(Math.min(results.value.tome_results.predOGT, 100)) : 0,
    
    // 耐盐性 - 根据新梯度分配分数
    results.value.salt_tolerance ? 
      (results.value.salt_tolerance.matched_sequences === 0 ? 0 :
       results.value.salt_tolerance.matched_sequences <= 2 ? 25 :
       results.value.salt_tolerance.matched_sequences <= 4 ? 50 :
       results.value.salt_tolerance.matched_sequences <= 6 ? 75 : 100) : 0,
    
    // 基因组完整度
    results.value.checkm2_results ? Number(results.value.checkm2_results.completeness) : 0,
    
    // 基因组污染度 - 直接使用污染度值并乘以10进行缩放（0.14% -> 1.4分, 10% -> 100分）
    results.value.checkm2_results ? Math.min(100, Number(results.value.checkm2_results.contamination) * 10) : 0,
    
    // 培养评估分数，新的评分标准
    results.value.checkm2_results && results.value.checkm2_results.cultivation_assessment ? 
      (results.value.checkm2_results.cultivation_assessment.cultivation_difficulty === "Cultivable" ? 100 :
       results.value.checkm2_results.cultivation_assessment.cultivation_difficulty === "Co-cultivation dependent" ? 50 : 15) : 0
  ]

  return {
    title: {
      text: 'Comprehensive Analysis Radar',
      left: 'center',
      textStyle: {
        fontFamily: 'Arial',
        fontSize: 16,
        color: '#000000'
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: function(params) {
        if (params.componentSubType === 'radar') {
          let result = params.name + '<br/>';
          params.value.forEach((val, index) => {
            result += indicators[index].name + ': ' + val + '<br/>';
          });
          return result;
        }
      }
    },
    legend: {
      data: ['Analysis Results'],
      bottom: 0,
      itemWidth: 15,
      itemHeight: 10,
      textStyle: {
        fontFamily: 'Arial',
        fontSize: 12,
        color: '#000000'
      }
    },
    grid: {
      right: '15%'
    },
    radar: {
      indicator: indicators,
      radius: '60%',
      splitNumber: 5,
      nameGap: 15,
      axisName: {
        fontFamily: 'Arial',
        color: '#000000',
        fontSize: 11,
        padding: [3, 5]
      },
      splitArea: {
        areaStyle: {
          color: ['rgba(255, 255, 255, 0.5)'],
          shadowColor: 'rgba(0, 0, 0, 0.2)',
          shadowBlur: 10
        }
      },
      axisLine: {
        lineStyle: {
          color: 'rgba(0, 0, 0, 0.2)'
        }
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(0, 0, 0, 0.2)'
        }
      },
      name: {
        textStyle: {
          fontSize: 12,
          fontFamily: 'Arial',
          color: '#000000'
        }
      }
    },
    series: [{
      name: 'Analysis Results',
      type: 'radar',
      data: [
        {
          value: values,
          name: 'Analysis Results',
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 2,
            color: '#409EFF'
          },
          areaStyle: {
            color: 'rgba(64, 158, 255, 0.3)'
          },
          label: {
            show: true,
            formatter: function(params) {
              return params.value.toFixed(1);
            },
            color: '#409EFF'
          }
        }
      ]
    }]
  }
})



// Download radar chart
const handleDownloadChart = (format) => {
  if (!radarChart.value) {
    ElMessage.error('Radar chart not initialized')
    return
  }
  
  try {
    const chart = radarChart.value
    
    // Get current date and time for filename
    const now = new Date()
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
    const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`
    const jobIdStr = jobId.value ? `_${jobId.value}` : ''
    
    if (format === 'png') {
      // Create a high-resolution PNG (300dpi)
      // Assuming 1 inch equals 96 pixels, 300dpi means a scale factor of 3.125
      const scale = 3.125
      const url = chart.getDataURL({
        pixelRatio: scale,
        backgroundColor: '#fff',
        type: 'png'
      })
      const fileName = `Metabolic_Analysis_Radar_${dateStr}_${timeStr}${jobIdStr}.png`
      downloadChart(url, fileName)
    } else if (format === 'svg') {
      try {
        // 获取完整的图表配置
        const option = chart.getOption()
        const width = chart.getWidth()
        const height = chart.getHeight()
        
        // 创建临时容器
        const container = document.createElement('div')
        container.style.visibility = 'hidden'
        container.style.position = 'fixed'
        container.style.width = width + 'px'
        container.style.height = height + 'px'
        document.body.appendChild(container)
        
        // 创建新的ECharts实例用于SVG渲染
        const tempChart = echarts.init(container, null, {
          renderer: 'svg',
          width: width,
          height: height
        })
        
        // 设置完整的图表配置
        tempChart.setOption({
          ...option,
          animation: false
        })
        
        // 等待图表渲染完成
        setTimeout(() => {
          try {
            const svgStr = container.querySelector('svg').outerHTML
            const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
            const url = URL.createObjectURL(blob)
            const fileName = `Metabolic_Analysis_Radar_${dateStr}_${timeStr}${jobIdStr}.svg`
            
            downloadChart(url, fileName)
            
            // 清理资源
            URL.revokeObjectURL(url)
            tempChart.dispose()
            document.body.removeChild(container)
            
            ElMessage.success(`Radar chart downloaded successfully as SVG`)
          } catch (error) {
            console.error('Error in SVG generation:', error)
            ElMessage.error('Error generating SVG, please try again')
          }
        }, 100)
        
      } catch (svgError) {
        console.error('SVG generation error:', svgError)
        ElMessage.error('Error generating SVG, please try again')
      }
    }
  } catch (error) {
    console.error('Error downloading radar chart:', error)
    ElMessage.error('Error downloading radar chart, please try again')
  }
}

// Download polar chart for methane pathways
const handleDownloadPolarChart = (format) => {
  if (!polarChart.value) {
    ElMessage.error('Polar chart not initialized')
    return
  }
  
  try {
    const chart = polarChart.value
    
    // Get current date and time for filename
    const now = new Date()
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
    const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`
    const jobIdStr = jobId.value ? `_${jobId.value}` : ''
    
    if (format === 'png') {
      // Create a high-resolution PNG (300dpi)
      const scale = 3.125
      const url = chart.getDataURL({
        pixelRatio: scale,
        backgroundColor: '#fff',
        type: 'png'
      })
      const fileName = `Methane_Pathways_Polar_${dateStr}_${timeStr}${jobIdStr}.png`
      downloadChart(url, fileName)
    } else if (format === 'svg') {
      try {
        // 获取完整的图表配置
        const option = chart.getOption()
        const width = chart.getWidth()
        const height = chart.getHeight()
        
        // 创建临时容器
        const container = document.createElement('div')
        container.style.visibility = 'hidden'
        container.style.position = 'fixed'
        container.style.width = width + 'px'
        container.style.height = height + 'px'
        document.body.appendChild(container)
        
        // 创建新的ECharts实例用于SVG渲染
        const tempChart = echarts.init(container, null, {
          renderer: 'svg',
          width: width,
          height: height
        })
        
        // 设置完整的图表配置
        tempChart.setOption({
          ...option,
          animation: false
        })
        
        // 等待图表渲染完成
        setTimeout(() => {
          try {
            const svgStr = container.querySelector('svg').outerHTML
            const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
            const url = URL.createObjectURL(blob)
            const fileName = `Methane_Pathways_Polar_${dateStr}_${timeStr}${jobIdStr}.svg`
            
            downloadChart(url, fileName)
            
            // 清理资源
            URL.revokeObjectURL(url)
            tempChart.dispose()
            document.body.removeChild(container)
            
            ElMessage.success(`Polar chart downloaded successfully as SVG`)
          } catch (error) {
            console.error('Error in SVG generation:', error)
            ElMessage.error('Error generating SVG, please try again')
          }
        }, 100)
        
      } catch (svgError) {
        console.error('SVG generation error:', svgError)
        ElMessage.error('Error generating SVG, please try again')
      }
    }
  } catch (error) {
    console.error('Error downloading polar chart:', error)
    ElMessage.error('Error downloading polar chart, please try again')
  }
}

// Download pie chart
const handleDownloadPieChart = (format) => {
  if (!pieChart.value) {
    ElMessage.error('Pie chart not initialized')
    return
  }
  
  try {
    const chart = pieChart.value
    
    // Get current date and time for filename
    const now = new Date()
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
    const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`
    const jobIdStr = jobId.value ? `_${jobId.value}` : ''
    
    if (format === 'png') {
      // Create a high-resolution PNG (300dpi)
      const scale = 3.125
      const url = chart.getDataURL({
        pixelRatio: scale,
        backgroundColor: '#fff',
        type: 'png'
      })
      const fileName = `Genome_Quality_Pie_${dateStr}_${timeStr}${jobIdStr}.png`
      downloadChart(url, fileName)
    } else if (format === 'svg') {
      try {
        // 获取完整的图表配置
        const option = chart.getOption()
        const width = chart.getWidth()
        const height = chart.getHeight()
        
        // 创建临时容器
        const container = document.createElement('div')
        container.style.visibility = 'hidden'
        container.style.position = 'fixed'
        container.style.width = width + 'px'
        container.style.height = height + 'px'
        document.body.appendChild(container)
        
        // 创建新的ECharts实例用于SVG渲染
        const tempChart = echarts.init(container, null, {
          renderer: 'svg',
          width: width,
          height: height
        })
        
        // 设置完整的图表配置
        tempChart.setOption({
          ...option,
          animation: false
        })
        
        // 等待图表渲染完成
        setTimeout(() => {
          try {
            const svgStr = container.querySelector('svg').outerHTML
            const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
            const url = URL.createObjectURL(blob)
            const fileName = `Genome_Quality_Pie_${dateStr}_${timeStr}${jobIdStr}.svg`
            
            downloadChart(url, fileName)
            
            // 清理资源
            URL.revokeObjectURL(url)
            tempChart.dispose()
            document.body.removeChild(container)
            
            ElMessage.success(`Pie chart downloaded successfully as SVG`)
          } catch (error) {
            console.error('Error in SVG generation:', error)
            ElMessage.error('Error generating SVG, please try again')
          }
        }, 100)
        
      } catch (svgError) {
        console.error('SVG generation error:', svgError)
        ElMessage.error('Error generating SVG, please try again')
      }
    }
  } catch (error) {
    console.error('Error downloading pie chart:', error)
    ElMessage.error('Error downloading pie chart, please try again')
  }
}

// Helper function to download chart
const downloadChart = (url, fileName) => {
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
</script>

<style scoped>
.diamond-analyzer-container {
  padding: 20px;
}

.content-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
}

h1 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.description {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.description h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
  border-left: 4px solid #409eff;
  padding-left: 10px;
}

.description p {
  color: #666;
  line-height: 1.6;
}

.feature-list {
  list-style-type: none;
  padding-left: 0;
}

.feature-list li {
  color: #666;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.feature-list li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.upload-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.upload-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
  border-left: 4px solid #409eff;
  padding-left: 10px;
}

.upload-info {
  color: #666;
  margin-bottom: 20px;
}

.progress-section {
  margin-top: 20px;
}

.progress-text {
  text-align: center;
  color: #666;
  margin-top: 10px;
}

.start-analysis-section {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.start-analysis-button {
  padding: 12px 30px;
  font-size: 16px;
}

.results-section {
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.results-section h2 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.5rem;
  border-left: 4px solid #409eff;
  padding-left: 10px;
}

.summary-card {
  background-color: #fff;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.summary-card h3 {
  color: #2c3e50;
  font-size: 1.3rem;
  margin-bottom: 10px;
}

.most-likely-pathway {
  background-color: #ecf5ff;
  border-left: 5px solid #409eff;
  padding: 15px;
  margin: 15px 0;
  border-radius: 4px;
}

.most-likely-pathway h4 {
  margin: 0 0 5px 0;
  font-size: 20px;
  color: #2c3e50;
}

.similarity-score {
  font-weight: bold;
  color: #409eff;
  font-size: 18px;
}

.warning-message {
  margin-bottom: 20px;
}

.highlight-score {
  color: #67C23A;
  font-weight: bold;
}

.salt-tolerance-card {
  margin-top: 20px;
}

.salt-tolerance-details {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.salt-level-display {
  margin-bottom: 15px;
  text-align: center;
}

.salt-level-value {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
}

.salt-ratio-text {
  font-size: 14px;
  color: #606266;
}

.salt-tolerance-info {
  text-align: center;
  width: 100%;
}

.levels-title {
  margin-bottom: 15px;
  font-size: 1.2rem;
  border-left: 4px solid #409eff;
  padding-left: 10px;
  text-align: left;
}

.salt-levels-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.salt-level-item {
  text-align: center;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  min-width: 160px;
  flex: 1 0 160px;
  max-width: 200px;
}

.level-name {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
}

.level-desc {
  font-size: 12px;
  color: #606266;
}

.active {
  font-weight: bold;
  transform: scale(1.05);
  background-color: rgba(240, 249, 235, 0.8);
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 颜色类 */
.extreme-color {
  color: #F56C6C;
}

.very-strong-color {
  color: #E6A23C;
}

.strong-color {
  color: #67C23A;
}

.moderate-color {
  color: #409EFF;
}

.no-resistance-color {
  color: #909399;
}

.extreme-color.active {
  border-left: 3px solid #F56C6C;
}

.very-strong-color.active {
  border-left: 3px solid #E6A23C;
}

.strong-color.active {
  border-left: 3px solid #67C23A;
}

.moderate-color.active {
  border-left: 3px solid #409EFF;
}

.no-resistance-color.active {
  border-left: 3px solid #909399;
}

.download-section {
  margin: 20px 0;
  display: flex;
  justify-content: center;
}

.download-summary-button {
  padding: 12px 30px;
  font-size: 14px;
}

.explanation-section {
  margin-top: 30px;
  background-color: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.explanation-section h3 {
  color: #2c3e50;
  font-size: 1.2rem;
  margin-bottom: 15px;
}

.explanation-section ul {
  padding-left: 20px;
  color: #606266;
}

.explanation-section li {
  margin-bottom: 8px;
  line-height: 1.5;
}

.explanation-section code {
  background-color: #f5f7fa;
  padding: 2px 5px;
  border-radius: 3px;
  font-family: monospace;
  color: #409eff;
}

.citation-section {
  margin-top: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.citation-section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.3rem;
}

.citation-content {
  color: #666;
  line-height: 1.6;
}

.citation-box {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin: 10px 0;
}

.citation-box a {
  color: #409EFF;
  text-decoration: none;
}

.citation-box a:hover {
  text-decoration: underline;
}

.license-text {
  margin-top: 10px;
  font-style: italic;
}

.license-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.usage-note {
  margin-top: 10px;
  font-size: 0.9em;
  color: #909399;
}

.paper-citation {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #e4e7ed;
  line-height: 1.6;
}

.paper-citation em {
  font-style: italic;
}

.repo-citation {
  margin-bottom: 0;
  line-height: 1.6;
}

.citation-box strong {
  color: #2c3e50;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 200px;
}

:deep(.el-upload__tip) {
  color: #666;
}

:deep(.el-progress-bar__inner) {
  transition: width 0.3s ease;
}

:deep(.el-table) {
  margin-top: 20px;
  margin-bottom: 20px;
}

:deep(.el-table th) {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

:deep(.el-table .el-button--primary) {
  padding: 8px 15px;
}

.additional-info {
  margin-top: 15px;
}

.top-pathways-list {
  list-style-type: none;
  padding-left: 0;
  margin-bottom: 10px;
}

.top-pathways-list li {
  padding: 5px 0;
  font-size: 18px;
  color: #2c3e50;
}

.checkm2-card {
  margin-top: 20px;
  background: #f9f9ff;
  border-left: 4px solid #409eff;
}

.checkm2-result {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.quality-metrics {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.quality-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.quality-indicator h4 {
  margin-bottom: 10px;
  color: #2c3e50;
}

.quality-summary {
  margin: 15px 0;
  text-align: center;
}

.quality-summary h4 {
  margin-bottom: 10px;
  color: #2c3e50;
}

.cultivation-assessment {
  margin: 15px 0;
  text-align: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
}

.cultivation-assessment h4 {
  margin-bottom: 10px;
  color: #2c3e50;
}

.cultivation-details {
  margin-top: 10px;
  font-size: 14px;
  color: #606266;
}

.cultivation-criteria {
  margin-top: 10px;
  font-size: 0.9em;
  color: #909399;
  background-color: #f8f9fa;
  padding: 8px;
  border-radius: 4px;
}

.success-text {
  color: #67C23A;
}

.warning-text {
  color: #E6A23C;
}

.danger-text {
  color: #F56C6C;
}

.pathway-type-title {
  margin-bottom: 10px;
  font-size: 1.5rem;
  border-left: 4px solid #409eff;
  padding-left: 10px;
}

.ogt-card {
  margin-top: 20px;
  background: #f9f9ff;
  border-left: 4px solid #409eff;
}

.ogt-result {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.ogt-details {
  margin-top: 10px;
  text-align: center;
}

.ogt-label {
  margin-bottom: 5px;
  font-weight: bold;
}

.ogt-value {
  font-size: 18px;
  font-weight: bold;
}

.ogt-confidence {
  margin-top: 5px;
  font-size: 0.9em;
  color: #909399;
}

.ogt-category {
  margin-top: 10px;
  font-size: 14px;
  color: #606266;
}

.radar-chart-container {
  margin: 20px 0;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
}

.radar-chart-wrapper {
  flex: 3.5;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.radar-chart {
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.polar-chart-wrapper {
  flex: 3.5;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.polar-chart {
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.pie-chart-wrapper {
  flex: 3.5;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pie-chart {
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.radar-scores {
  flex: 3;
  border-left: 1px solid #eee;
  padding-left: 20px;
  margin-left: 20px;
  align-self: flex-start;
}

.radar-scores h4 {
  margin-bottom: 12px;
  color: #2c3e50;
  font-size: 1rem;
  text-align: center;
}

.score-list {
  list-style-type: none;
  padding-left: 0;
}

.score-list li {
  margin-bottom: 8px;
  padding: 6px;
  border-radius: 4px;
  background-color: #f9f9f9;
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
}

.indicator-name {
  font-weight: bold;
  color: #606266;
  font-size: 0.9rem;
}

.indicator-value {
  font-weight: bold;
  font-size: 0.9rem;
}

.success {
  color: #67C23A;
}

.warning {
  color: #E6A23C;
}

.danger {
  color: #F56C6C;
}

.chart-actions {
  margin-top: 20px;
  text-align: center;
  width: 100%;
  display: flex;
  justify-content: center;
}

.download-chart-button {
  padding: 8px 15px;
}
</style>